import * as THREE from 'three';
import { CharacterRig } from './CharacterRig';
import { AABB, PlayBounds } from './types';

/** Soft companion — ICO-style side follow when holding hands. */
export class Companion {
  readonly rig = new CharacterRig({ companion: true });
  readonly position = new THREE.Vector3();
  private readonly velocity = new THREE.Vector3();
  private yaw = 0;
  private held = false;
  private bounds: PlayBounds | null = null;
  private solids: AABB[] = [];
  private readonly spawnPoint = new THREE.Vector3();
  private readonly desired = new THREE.Vector3();
  private readonly tmp = new THREE.Vector3();
  private readonly radius = 0.22;

  constructor(spawn: THREE.Vector3, bounds?: PlayBounds) {
    this.spawnPoint.copy(spawn);
    this.position.copy(spawn);
    this.rig.root.position.copy(spawn);
    this.bounds = bounds ?? null;
  }

  setBounds(bounds: PlayBounds): void {
    this.bounds = bounds;
  }

  setSolids(solids: AABB[]): void {
    this.solids = solids;
  }

  /** Take / release hand — tighter side follow + held anims. */
  setHeld(v: boolean): void {
    this.held = v;
    this.rig.setHeldMode(v);
    if (v) {
      this.rig.beginTakeHand();
    } else if (
      this.rig.getState() === 'holdIdle' ||
      this.rig.getState() === 'holdWalk' ||
      this.rig.getState() === 'holdRun' ||
      this.rig.getState() === 'takeHand'
    ) {
      this.rig.setState('idle');
    }
  }

  isHeld(): boolean {
    return this.held;
  }

  /**
   * @param playerYaw Mixamo +Z facing of the player — used for side-hand slot.
   * @param playerHoriz player's horizontal speed (sync hold-walk while tethered).
   * @param playerRunning whether the player is sprinting (hold-run sync).
   */
  update(
    dt: number,
    playerPos: THREE.Vector3,
    playerYaw = 0,
    playerHoriz = 0,
    playerRunning = false,
  ): void {
    if (this.held) {
      this.updateHeld(dt, playerPos, playerYaw, playerHoriz, playerRunning);
    } else {
      this.updateFollow(dt, playerPos);
    }

    this.clampToBounds();

    this.rig.root.position.copy(this.position);
    this.rig.root.rotation.y = this.yaw;
    const horiz = Math.hypot(this.velocity.x, this.velocity.z);
    this.rig.update(dt, Math.max(horiz, playerHoriz));
  }

  private updateHeld(
    dt: number,
    playerPos: THREE.Vector3,
    playerYaw: number,
    playerHoriz: number,
    playerRunning: boolean,
  ): void {
    // Stand at player's left (player LeftHand ↔ companion RightHand).
    const leftX = -Math.cos(playerYaw);
    const leftZ = Math.sin(playerYaw);
    const forwardX = Math.sin(playerYaw);
    const forwardZ = Math.cos(playerYaw);
    this.desired.set(
      playerPos.x + leftX * 0.78 - forwardX * 0.08,
      playerPos.y,
      playerPos.z + leftZ * 0.78 - forwardZ * 0.08,
    );

    this.tmp.subVectors(this.desired, this.position);
    this.tmp.y = 0;
    const dist = this.tmp.length();
    if (dist > 0.04) {
      this.tmp.normalize();
      const maxSpeed = playerRunning ? 5.2 : 4.4;
      const speed = Math.min(maxSpeed, dist * 6.5 + 0.4);
      this.velocity.x = this.tmp.x * speed;
      this.velocity.z = this.tmp.z * speed;
    } else {
      this.velocity.x *= Math.pow(0.001, dt);
      this.velocity.z *= Math.pow(0.001, dt);
      if (Math.hypot(this.velocity.x, this.velocity.z) < 0.04) {
        this.velocity.x = 0;
        this.velocity.z = 0;
      }
    }

    this.yaw = this.lerpAngle(this.yaw, playerYaw, 1 - Math.pow(0.002, dt));

    this.position.x += this.velocity.x * dt;
    this.position.z += this.velocity.z * dt;
    this.position.y += (playerPos.y - this.position.y) * Math.min(1, dt * 5);
    this.resolveSolids();

    if (this.rig.isTakingHand()) return;

    if (playerHoriz > 0.35 || dist > 0.22) {
      this.rig.setState(playerRunning ? 'holdRun' : 'holdWalk');
    } else {
      this.rig.setState('holdIdle');
    }
  }

  private updateFollow(dt: number, playerPos: THREE.Vector3): void {
    const followDist = 2.4;
    const to = this.tmp.subVectors(playerPos, this.position);
    to.y = 0;
    const dist = to.length();

    if (dist > followDist + 0.05) {
      to.normalize();
      const maxSpeed = 3.2;
      const slowRadius = 2.6;
      const excess = dist - followDist;
      const speed = Math.min(maxSpeed, (excess / slowRadius) * maxSpeed + 0.6);
      this.velocity.x = to.x * speed;
      this.velocity.z = to.z * speed;
      const targetYaw = Math.atan2(to.x, to.z);
      this.yaw = this.lerpAngle(this.yaw, targetYaw, 1 - Math.pow(0.001, dt));
      this.rig.setState(speed > 3.6 ? 'run' : 'walk');
    } else {
      this.velocity.x *= Math.pow(0.001, dt);
      this.velocity.z *= Math.pow(0.001, dt);
      if (Math.hypot(this.velocity.x, this.velocity.z) < 0.05) {
        this.velocity.x = 0;
        this.velocity.z = 0;
      }
      this.rig.setState('idle');
    }

    this.position.x += this.velocity.x * dt;
    this.position.z += this.velocity.z * dt;
    this.position.y += (playerPos.y - this.position.y) * Math.min(1, dt * 4);
    this.resolveSolids();
  }

  /** Simple torso AABB vs world solids so Mira doesn't walk through walls. */
  private resolveSolids(): void {
    const height = 1.45;
    for (let iter = 0; iter < 4; iter++) {
      const minX = this.position.x - this.radius;
      const maxX = this.position.x + this.radius;
      const minY = this.position.y + 0.2;
      const maxY = this.position.y + height;
      const minZ = this.position.z - this.radius;
      const maxZ = this.position.z + this.radius;
      let moved = false;
      for (const s of this.solids) {
        if (maxX <= s.minX || minX >= s.maxX || maxY <= s.minY || minY >= s.maxY || maxZ <= s.minZ || minZ >= s.maxZ) {
          continue;
        }
        const ox = Math.min(maxX - s.minX, s.maxX - minX);
        const oy = Math.min(maxY - s.minY, s.maxY - minY);
        const oz = Math.min(maxZ - s.minZ, s.maxZ - minZ);
        if (oy < ox && oy < oz && oy < 0.35) {
          // Standing on top
          this.position.y = s.maxY + 0.01;
        } else if (ox < oz) {
          this.position.x += this.position.x < (s.minX + s.maxX) * 0.5 ? -ox - 0.01 : ox + 0.01;
          this.velocity.x = 0;
          moved = true;
        } else {
          this.position.z += this.position.z < (s.minZ + s.maxZ) * 0.5 ? -oz - 0.01 : oz + 0.01;
          this.velocity.z = 0;
          moved = true;
        }
      }
      if (!moved) break;
    }
  }

  private lerpAngle(a: number, b: number, t: number): number {
    let diff = b - a;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    return a + diff * Math.min(1, t);
  }

  private clampToBounds(): void {
    const b = this.bounds;
    if (!b) return;
    if (this.position.y < b.killY) {
      this.position.copy(this.spawnPoint);
      this.velocity.set(0, 0, 0);
      return;
    }
    const pad = 0.35;
    this.position.x = Math.min(b.maxX - pad, Math.max(b.minX + pad, this.position.x));
    this.position.z = Math.min(b.maxZ - pad, Math.max(b.minZ + pad, this.position.z));
  }
}
